import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { LoginService } from '../../services/login/login.service';
import { AuthService } from '../../services/auth/auth.service';
import { UserProfileService, UserProfile } from '../../services/user-profile/user-profile.service';
import { Subscription } from 'rxjs';
import { filter, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-profile-menu',
  standalone: true,
  imports: [
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    CommonModule,
    RouterModule,
  ],
  templateUrl: './profile-menu.html',
  styleUrls: ['./profile-menu.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProfileMenu implements OnInit, OnDestroy {
  userProfile: UserProfile | null = null;
  loading = false;
  private subscriptions = new Subscription();

  constructor(
    private loginService: LoginService,
    private authService: AuthService,
    private router: Router,
    private userProfileService: UserProfileService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    // Subscribe to user profile from shared service
    this.subscriptions.add(
      this.userProfileService.getUserProfile().subscribe((profile) => {
        this.userProfile = profile;
      }),
    );

    // Subscribe to loading state
    this.subscriptions.add(
      this.userProfileService.isLoading().subscribe((loading) => {
        this.loading = loading;
      }),
    );

    // Fetch profile immediately on init if authenticated (handles page refresh)
    if (isPlatformBrowser(this.platformId) && this.authService.isAuthenticated()) {
      // Force refresh to ensure we get the latest data on page load
      this.userProfileService.fetchUserProfile(true).subscribe();
    }

    // Refresh profile when navigating to protected routes (after login)
    // This ensures profile is fetched on each page navigation
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          debounceTime(300),
        )
        .subscribe(() => {
          // Fetch profile on each navigation if authenticated
          // This ensures profile is loaded even if backend was down and came back online
          if (isPlatformBrowser(this.platformId) && this.authService.isAuthenticated()) {
            // Check if profile is missing or cache might be expired
            // fetchUserProfile will respect cache but will fetch if needed
            const currentProfile = this.userProfileService.getCurrentProfile();
            if (!currentProfile) {
              // No profile loaded, fetch immediately
              this.userProfileService.fetchUserProfile(true).subscribe();
            } else {
              // Profile exists, but refresh to ensure it's up to date (respects cache)
              this.userProfileService.fetchUserProfile(false).subscribe();
            }
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Get user's full name
   */
  get fullName(): string {
    // Show loading state while fetching
    if (this.loading) {
      return '';
    }

    if (!this.userProfile) {
      return 'Guest';
    }

    return this.userProfile.full_name || 'Guest';
  }

  /**
   * Get user's email
   */
  get email(): string {
    return this.userProfile?.email || '';
  }

  /**
   * Get user initials
   * - If full_name has only one word: show first letter
   * - If full_name has two or more words: show first letter of first word + first letter of second word
   */
  get initials(): string {
    // Show loading state while fetching
    if (this.loading) {
      return '';
    }

    if (!this.userProfile || !this.userProfile.full_name) {
      // Fallback to email first letter if available
      if (this.userProfile?.email && this.userProfile.email[0]) {
        return this.userProfile.email[0].toUpperCase();
      }
      return '';
    }

    const fullName = this.userProfile.full_name.trim();
    if (!fullName) {
      return '';
    }

    // Split by spaces and filter out empty strings
    const parts = fullName.split(/\s+/).filter((p) => p.length > 0);

    if (parts.length === 0) {
      return 'G';
    } else if (parts.length === 1) {
      // Only one word: show first letter
      const firstPart = parts[0];
      return firstPart && firstPart[0] ? firstPart[0].toUpperCase() : 'G';
    } else {
      // Two or more words: show first letter of first word + first letter of second word
      const firstPart = parts[0];
      const secondPart = parts[1];
      const firstChar = firstPart?.[0] || '';
      const secondChar = secondPart?.[0] || '';
      if (firstChar && secondChar) {
        return (firstChar + secondChar).toUpperCase();
      }
      return firstChar ? firstChar.toUpperCase() : 'G';
    }
  }

  /**
   * Get background color for avatar based on initials
   */
  get avatarColor(): string {
    // Generate a consistent color based on initials
    const defaultColor = '#5035AB'; // Purple
    const colors: string[] = [
      defaultColor,
      '#6454CD', // Light purple
      '#7C3AED', // Violet
      '#8B5CF6', // Light violet
      '#A78BFA', // Lavender
    ];

    if (this.initials && this.initials.length > 0) {
      const index = this.initials.charCodeAt(0) % colors.length;
      if (index >= 0 && index < colors.length && colors[index]) {
        return colors[index];
      }
    }

    return defaultColor;
  }

  logout(): void {
    // Use AuthService to properly clear authentication token
    this.authService.logout();
    // Update the login signal to false
    this.loginService.loginStateChange(false);
    // Clear user profile from service
    this.userProfileService.clearProfile();
    // Navigate to login page
    this.router.navigate(['/login'], { replaceUrl: true });
  }
}
